import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { FilterOutlined } from '@ant-design/icons';
import {
    Button,
    EmptyState,
    ErrorState,
    FilterPopover,
    SectionCard,
    useToast,
    type ColumnConfig,
    type DropdownMenuItem,
    type StatusConfig,
} from '@/components/ui';
// EAA-1878: this tab renders through the SAME accounts chrome the realm and org
// account screens use (`accounts/shared/`), not a bare `DataTable`. The shared
// component was built for exactly this — its own docstring says column CONTENT
// stays context-owned while the chrome around it is shared — so adopting it
// gives this tab selection checkboxes, the same sort-caret affordance, the same
// row kebab, the same single scroll region and the same pager as
// `/console/accounts`, instead of a second hand-tuned equivalent that drifts.
import { AccountsTable as SharedAccountsTable } from '../Accounts/Shared/AccountsTable';
import { AccountsPagination } from '../Accounts/Shared/AccountsPagination';
// EAA-1839: and through the SAME toolbar too. This tab used to hand-roll its own
// search `Input` (plus a hand-drawn magnifier `<svg>` and a hand-rolled "Xoá bộ
// lọc" `<button>`), which meant it opted out of every capability the shared
// chrome grew this session: the "Hiển thị" popover (column visibility + row
// density), the refresh glyph, the range readout and the active-filter chip.
// All four are optional props on the shared component, so adopting it is a
// deletion here, not an addition — and the search box now looks and behaves
// like the one on `/console/accounts` instead of being a fourth variant.
import { AccountsToolbar } from '../Accounts/Shared/AccountsToolbar';
// EAA-1839 (2026-08-24, user request): "replace filter by common account table".
// The three bespoke `AppsFilterButton` dropdowns and the six bespoke outcome
// chips are GONE; every filter this tab offers is now a `FacetGroup` rendered by
// the shared `AccountsFacetRail` — the same component, the same shape and the
// same counted-checkbox affordance both accounts boards use. `AccountsBulkBar`
// comes along for the same reason: a multi-row action belongs in the shared
// selection bar, not in a row of controls only this screen has.
import { AccountsBulkBar } from '../Accounts/Shared/AccountsBulkBar';
import { AccountsFacetRail } from '../Accounts/Shared/AccountsFacetRail';
import type { FacetGroup } from '../Accounts/Shared/facetTypes';
import { IdentityCell, StatusChip, type CellTone } from '../Accounts/Shared/cells';
import { APP_DETAIL_ACCOUNTS_TABLE_KEY, useAppDetailAccountsView } from './useAppDetailAccountsView';
import { isUnassignedRoleKey } from '@/pages/Console/Accounts/Shared/roleKeyDisplay';
import { retryProvisioning, type ProvisioningOutcome } from '@/api/console';
import { accountDisplayName } from '../Accounts/Profile/accountDisplayName';
import type { AppDetailAccountRow } from './useAppDetailPage';
import type { AppDetailPageVM } from './useAppDetailPage';
import detail from './AppDetail.module.css';
import styles from './AppDetailAccountsTab.module.css';

// EAA-1817 (frame 53): the full outcome vocabulary provisioning.py can persist
// (see `PUSHED`/`REVOKED`/`SKIPPED_*`/`DRIFT`/`PENDING_REVOKE`/`ERROR` in
// portal-api's `directory/provisioning.py`) — `never_attempted` is the
// client-side default `outcomes_for_org` substitutes for a null `last_outcome`
// and is already part of `ProvisioningOutcome` (see `@/api/console`).
const OUTCOME_TONE: Record<ProvisioningOutcome, StatusConfig['tone']> = {
    pushed: 'success',
    revoked: 'default',
    no_mapping: 'warning',
    paused_mapping: 'warning',
    ambiguous_mapping: 'warning',
    drift: 'warning',
    pending_revoke: 'processing',
    error: 'error',
    never_attempted: 'default',
};

/** EAA-1878: the 9 provisioning outcomes collapsed onto `shared/cells`' 3-tone
 *  vocabulary, the same way the realm screen maps enabled/pending/disabled and
 *  the org screen maps ok/pending/sync-error/revoked. The mapping is domain
 *  logic and stays here; only the dot+label widget is shared. `error` is `warn`,
 *  not `off` — `off` is the muted "nothing happened here" tone (revoked, never
 *  attempted), and a failure must not read as quieter than a stale mapping. */
const OUTCOME_CELL_TONE: Record<ProvisioningOutcome, CellTone> = {
    pushed: 'ok',
    revoked: 'off',
    never_attempted: 'off',
    no_mapping: 'warn',
    paused_mapping: 'warn',
    ambiguous_mapping: 'warn',
    drift: 'warn',
    pending_revoke: 'warn',
    error: 'warn',
};

// EAA-1888: outcomes that never count toward "needs attention" — matches
// `AssignmentService.outcomes_for_app`'s own `needs_attention` sum exactly,
// so the retry button's count and the facet's count can never disagree.
const HEALTHY_OUTCOMES = new Set(['pushed', 'revoked', 'never_attempted']);

/* EAA-1839 (2026-08-24, user request) — REMOVED, recorded not erased.
   `QUICK_FILTER_OUTCOMES` used to list the six outcomes that got a one-click
   chip in a bespoke row under the toolbar (EAA-1840, 2026-08-15 design
   revision): pushed / no_mapping / paused_mapping / ambiguous_mapping /
   pending_revoke / error, deliberately NOT revoked, never_attempted or drift,
   which were reachable only through the "Kết quả" dropdown's full list.

   The user asked for this tab's filtering to be the shared accounts chrome, and
   the chip row mapped onto it exactly: chips WERE outcome options with counts,
   which is precisely a `FacetGroup`. The shared facet rail now shows all NINE
   outcomes with their counts, so the split between "chip-worthy" and
   "dropdown-only" outcomes has no reason to exist any more — it existed because
   a chip row has a width limit, and a rail does not. Nothing is less reachable:
   what was one click is now two (open "Bộ lọc", tick), and three outcomes that
   were two clicks are now also two.

   Do not reintroduce the chip row without checking with the user first — it has
   now been deliberately removed once, the same way the accounts boards' saved-
   filter row was removed twice (see `useScopedAccountViews`'s docstring). */

/**
 * EAA-1839: COLUMN WIDTHS ARE A BUDGET, and this comment records the arithmetic
 * so the next editor does not have to rediscover it the way the org board did.
 *
 * `DataTable` hands antd a `scroll.x` (default 900), which puts the table on
 * `table-layout: fixed`. Under fixed layout a column with a declared width takes
 * exactly that; a column WITHOUT one takes only what is left over — and if the
 * declared widths already sum to the table's width, it gets ZERO. That is not
 * hypothetical: it shipped on `/console/hierarchy/accounts`, where the identity
 * column collapsed to 0px, its avatar painted over its own text and the header
 * cell wrapped one letter per line at 199px tall.
 *
 * TWO columns are deliberately left without a width — the identity cell and the
 * role cell — so they share the leftover. That is one more than the accounts
 * boards leave, and on purpose: those two hold the only genuinely variable text
 * on the row (a display name over an email; `role_key → app roles`), while the
 * other four are as wide as their HEADINGS need and no wider.
 *
 * WIDTHS ARE SET BY THE HEADER, NOT THE CONTENT, because a header that wraps is
 * what turns a 47px header row into a 90px (or 199px) one. Measured in the
 * running app at 600 14px Open Sans, plus 40px of antd cell padding, plus the
 * resize grip (~9px) or the sort caret + grip (~32px) where present:
 *
 *   column          text   needs   set to
 *   Tổ chức·phạm vi  118     167     172
 *   Kết quả lần cuối 109     158     162
 *   Thời điểm         68     140     148   (sortable: caret + grip)
 *   Số lần thử        69     141     148   (sortable: caret + grip)
 *   declared total                   630
 *   selection column                  36
 *   actions column                   120   (`AccountsTable`'s `actionsWidth`)
 *   committed                        786
 *
 * Leftover for the two auto columns, measured: 442px at a 1366px viewport
 * (container 1228) and 516px at 1440px (container 1302). Neither auto column can
 * reach 0 until the table itself drops below 786px — and antd floors it at
 * `scroll.x` = 900, which is only in play below roughly a 1040px viewport, a
 * width this console shell does not support. Below that the cells CLIP rather
 * than overlap (`AccountsTable` sets `styles.body.cell.overflow: hidden`).
 *
 * If a fifth fixed column is ever added here, re-run this arithmetic first: the
 * committed total must stay comfortably under 900, not merely under whatever the
 * container happens to measure today.
 */
const COLUMN_WIDTHS = {
    organization: 172,
    outcome: 162,
    outcome_at: 148,
    sync_attempts: 148,
} as const;

/**
 * EAA-1839 (2026-08-24, user request): the SAME four columns, sized for the
 * `'sidebar'` facet-rail placement — the second budget the "VỊ TRÍ BỘ LỌC"
 * option makes necessary.
 *
 * A 236px rail column (plus its 14px gutter) takes the table from 1228px to
 * 978px at a 1366px viewport. Reusing `COLUMN_WIDTHS` there would leave
 * 978 − 36 (selection) − 120 (actions) − 630 (fixed) = 192px for the TWO
 * width-less columns, i.e. ~96px each for the identity cell and the role cell —
 * the two that hold the only genuinely variable text on the row. That is the
 * broken layout this file's earlier note predicted, and it is why the option was
 * previously declined outright rather than shipped.
 *
 * These numbers buy it back: 140 + 132 + 116 + 112 = 500 fixed, leaving 322px
 * for the pair, ~161px each — narrower than the 221px they get today, but a
 * legible cell rather than a clipped one. Nothing overflows either
 * (500 + 36 + 120 = 656 < 968), which matters because `.acc-table-scroll` is
 * `overflow-x: hidden`: a table wider than its container loses its rightmost
 * columns rather than gaining a scrollbar.
 *
 * WHAT IT COSTS, stated so nobody "fixes" it: all four headings wrap to two
 * lines at these widths (they need 167/158/140/141px to stay on one), so the
 * header row grows from 47px to ~66px. A heading is read once and a cell is
 * read on every row, and this is an opt-in placement the operator chose — but if
 * a future pass wants the one-line header back, the answer is to leave the rail
 * in the popover, not to widen these.
 */
const SIDEBAR_COLUMN_WIDTHS = {
    organization: 140,
    outcome: 132,
    outcome_at: 116,
    sync_attempts: 112,
} as const;

/** Row shape fed to `DataTable` — the raw outcome row plus a few precomputed
 *  strings the column `render`s and the toolbar's search derive from. */
interface AccountTableRow extends AppDetailAccountRow {
    displayName: string;
    appRoleLabel: string;
}

export interface AppDetailAccountsTabProps {
    vm: AppDetailPageVM;
    /** EAA-1861: present only when the caller has resolved
     *  `console.apps.sync_users` AND the app is not internal — same
     *  fail-closed convention `AppDetailHeader`'s own `onSyncUsers` uses. */
    onSyncUsers?: () => void;
}

/**
 * EAA-1840 (2026-08-15 design revision, frame 53 "Tài khoản" tab): accounts
 * with access to this app, derived from provisioning outcomes across EVERY
 * organization — NOT a raw user list (no such list exists for an app).
 *
 * EAA-1888: this tab now reads a genuinely server-paginated/filtered/searched
 * list (`GET /admin/apps/{code}/accounts`, `useAppDetailPage`'s
 * `accountsQuery`) instead of fetching EVERY organization's full outcome list
 * and slicing it in the browser. Search/outcome/org/role filters and the
 * "Tất cả (N)" counts all come from the server now (`vm.accountsFacets`) —
 * this component has nothing left to filter or aggregate client-side beyond
 * per-row display formatting (`displayName`/`appRoleLabel`).
 *
 * EAA-1839: THE CHROME IS NOW ENTIRELY SHARED — `Accounts/Shared/`'s
 * `AccountsToolbar` + `AccountsTable` + `AccountsPagination`. What this tab
 * hand-rolled before was the search row: its own `Input`, its own magnifier
 * `<svg>` and its own "Xoá bộ lọc" button, which is why it had none of the
 * capabilities the shared toolbar grew (column visibility, row density,
 * refresh, the range readout, the active-filter chip). Those are optional props
 * on the shared component, so this tab now passes them and deletes its own.
 *
 * NOT `Accounts/Shared/AccountsScreen`, deliberately: that shell owns a
 * page-level tab strip and a facet rail, and this tab already lives inside
 * `AppDetailPage`'s tab strip — adopting it would nest two tab strips. Toolbar,
 * table and pager only.
 *
 * EAA-1839 (2026-08-24, user request): AND THE FILTERS ARE SHARED TOO. This tab
 * used to draw three `AppsFilterButton` dropdowns (borrowed from `apps/`) plus a
 * six-chip outcome row of its own. Both are gone: every filter is now a
 * `FacetGroup` rendered by the shared `AccountsFacetRail`, collapsed behind the
 * toolbar's "Bộ lọc (N)" trigger — see `facetGroups` and `filterTrigger` below
 * for the mapping, the single-select semantics, and why the rail is in a popover
 * rather than a sidebar column. `DataTable`'s own generic built-in toolbar
 * (`showToolbar`) was tried long before either of these and dropped — a plain
 * search box plus antd `Select`s with no counts at all.
 *
 * Known gaps recorded in `docs/SRS-Portal-Apps-RoleMap-Frontend.md`, not
 * papered over: (13) the persisted outcome/timestamp/retry-count/fix are
 * per-ASSIGNMENT (the worst case across every app the org's Level fans out
 * to), not per-app — a row here may describe a different app's failure when
 * the same Level also grants elsewhere. The frame's "Chi tiết vòng chạy"
 * button is omitted, not fabricated — there is no persisted "sync run"
 * record for provisioning the way there is for catalog sync, so there is
 * nothing real for it to open. EAA-1888 adds one more: the "Thử lại" button
 * now retries only the CURRENT PAGE's failing rows, not every failing row
 * for this app across every organization — that app-wide sweep needed the
 * full cross-org row list client-side, which is exactly the load this
 * ticket removes. A real cross-org bulk-retry is a follow-up, not silently
 * kept by leaving the old fetch in place.
 */
export function AppDetailAccountsTab({ vm, onSyncUsers }: AppDetailAccountsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const outcomeConfig = useMemo<Record<string, StatusConfig>>(() => {
        const labels: Record<ProvisioningOutcome, string> = {
            pushed: t('console.appDetail.accounts.outcome.pushed'),
            revoked: t('console.appDetail.accounts.outcome.revoked'),
            no_mapping: t('console.appDetail.accounts.outcome.noMapping'),
            paused_mapping: t('console.appDetail.accounts.outcome.pausedMapping'),
            ambiguous_mapping: t('console.appDetail.accounts.outcome.ambiguousMapping'),
            drift: t('console.appDetail.accounts.outcome.drift'),
            pending_revoke: t('console.appDetail.accounts.outcome.pendingRevoke'),
            error: t('console.appDetail.accounts.outcome.error'),
            never_attempted: t('console.appDetail.accounts.outcome.neverAttempted'),
        };
        return Object.fromEntries(
            (Object.keys(labels) as ProvisioningOutcome[]).map((key) => [
                key,
                { label: labels[key], tone: OUTCOME_TONE[key] },
            ])
        );
    }, [t]);

    // EAA-1817: reverse of `AppRoleConversion.convertedTo` (app role -> standard
    // role keys) — the design's "vai trò chuẩn → vai trò trong app" column reads
    // the other direction (standard role key -> this app's role(s)). Independent
    // of EAA-1888's server-side RELEVANCE filter: this is purely a display lookup
    // for whichever rows the server already decided to return.
    const appRolesByRoleKey = useMemo(() => {
        const map: Record<string, string[]> = {};
        vm.roleConversions.forEach((role) => {
            role.convertedTo.forEach((roleKey) => {
                map[roleKey] = [...(map[roleKey] ?? []), role.key];
            });
        });
        return map;
    }, [vm.roleConversions]);

    const rows: AccountTableRow[] = useMemo(
        () =>
            vm.accountRows.map((row) => ({
                ...row,
                // EAA-1839: the real `username` goes INTO the helper rather than
                // being blanked here and OR-ed in at the cell. `accountDisplayName`
                // already encodes the precedence — name → username → email — so
                // giving it the whole row makes this board resolve identity by
                // exactly the same rule as the realm board.
                //
                // It previously passed `username: ''` and the cell rendered
                // `row.username || row.displayName`, i.e. username FIRST — the
                // inverse of the realm board. It looked correct only because
                // `AppAccountRow.username` arrives empty here in practice (see its
                // docstring: empty when Keycloak has no user for `user_sub`), so the
                // name won by accident. The moment that field is populated the two
                // screens would have shown the same person differently.
                displayName: accountDisplayName({
                    first_name: row.first_name,
                    last_name: row.last_name,
                    username: row.username,
                    email: row.user_email,
                }),
                appRoleLabel: (appRolesByRoleKey[row.role_key ?? ''] ?? []).join(', '),
            })),
        [vm.accountRows, appRolesByRoleKey]
    );

    const view = useAppDetailAccountsView();
    const { isColumnVisible } = view;

    // EAA-1888: debounced so typing a search term fires one server request per
    // pause, not one per keystroke — `vm.accountsSearch`/`setAccountsSearch`
    // drive a real query, unlike the pre-1888 instant client-side filter.
    //
    // EAA-1839: a VALUE-level debounce now, not the kit's `useDebouncedSearch`.
    // That hook hands back an `onChange` for an `Input` it owns; the shared
    // `AccountsToolbar` owns the input and exposes `onSearchChange(value:
    // string)`, so wrapping the plain string back into a synthetic
    // `ChangeEvent` just to satisfy the hook would be worse than the four lines
    // below. Same shape as `useScopedAccounts`/`useAuditFilters`, which reached
    // the same conclusion for the same reason.
    const [searchInput, setSearchInput] = useState(vm.accountsSearch);
    const { setAccountsSearch } = vm;
    useEffect(() => {
        if (searchInput === vm.accountsSearch) return;
        const timer = window.setTimeout(() => setAccountsSearch(searchInput), 300);
        return () => window.clearTimeout(timer);
        // `vm.accountsSearch` is read only to skip a redundant set; re-running
        // this effect when it lands would restart the timer for no reason.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput, setAccountsSearch]);

    /** The shared facet rail's own client-side option filter — it narrows the
     *  RAIL (which carries every organization and every role in the facets),
     *  never the table. Per-visit, so deliberately not persisted. */
    const [railQuery, setRailQuery] = useState('');
    // EAA-1878: row selection, the capability adopting `shared/AccountsTable`
    // brings. It is what makes "Thử lại" a deliberate act on chosen rows rather
    // than "whatever happens to be on this page" — see `retryTargets` below.
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    const filtersActive = Boolean(
        vm.accountsSearch || vm.accountsOutcomeFilter || vm.accountsOrgFilter || vm.accountsRoleFilter
    );
    // EAA-1839: the shared toolbar's active-filter chip is a COUNT, not a
    // boolean — it reads "Bộ lọc (2) · Xoá", so it has to know how many of the
    // four independent narrowings are on, not merely that one is.
    const activeFilterCount =
        (vm.accountsSearch ? 1 : 0) +
        (vm.accountsOutcomeFilter ? 1 : 0) +
        (vm.accountsOrgFilter ? 1 : 0) +
        (vm.accountsRoleFilter ? 1 : 0);
    function clearFilters() {
        // The input is locally controlled (see the debounce above), so clearing
        // the query alone would leave the cleared term still visible in the box.
        setSearchInput('');
        vm.setAccountsSearch('');
        vm.setAccountsOutcomeFilter(null);
        vm.setAccountsOrgFilter(null);
        vm.setAccountsRoleFilter(null);
    }

    // EAA-1839: the shared toolbar's refresh glyph. `useAppDetailPage` exposes no
    // `refetch`, and this tab does not own that file — invalidating the query key
    // the tab already invalidates after a retry does the same job, and
    // `useIsFetching` on that key is what makes the button's spinner honest for a
    // BACKGROUND refetch (`vm.accountsLoading` is `isLoading`, which is false
    // once there is cached data and so would never spin here).
    const accountsFetching = useIsFetching({ queryKey: ['app-accounts'] }) > 0;
    const handleRefresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['app-accounts'] });
    }, [queryClient]);

    // EAA-1888: every count below comes from `vm.accountsFacets` — computed
    // server-side over the app-relevant, visibility-scoped set, NEVER narrowed
    // by whichever filter is currently applied (see `AssignmentService.
    // outcomes_for_app`'s own docstring).
    // EAA-1878: `AccountsPagination` renders "Hiển thị {from}–{to} trong {total}"
    // and takes the range as inputs, because each adapter computes it from what
    // it actually knows server-side (see the component's own docstring).
    const rangeFrom = vm.accountsTotal === 0 ? 0 : (vm.accountsPage - 1) * vm.accountsPageSize + 1;
    const rangeTo = Math.min(vm.accountsPage * vm.accountsPageSize, vm.accountsTotal);

    const facets = vm.accountsFacets;
    // EAA-1888: `?? {}` produces a fresh object every render when `facets` is
    // absent, so the fallback itself is memoized on `facets` — otherwise every
    // consumer below would see a new object on each render while loading.
    const outcomeCounts = useMemo(() => facets?.outcome_counts ?? {}, [facets]);
    // EAA-1839: the `allRowsTotal` sum that used to live here fed the toolbar's
    // "Hiển thị {shown}/{total}" readout, which was removed as a duplicate of the
    // footer's range. Nothing else consumed it; `outcomeCounts` still feeds the
    // per-outcome facet counts.
    // EAA-1839: memoized for the same reason `outcomeCounts` above is — `?? []`
    // is a fresh array every render, and `facetGroups` below now depends on it.
    const orgOptions = useMemo(() => facets?.organizations ?? [], [facets]);
    const roleOptions = useMemo(
        () =>
            (facets?.roles ?? [])
                .filter((role): role is { role_key: string; count: number } => role.role_key !== null)
                .map((role) => ({
                    key: role.role_key,
                    count: role.count,
                    // EAA-1861 / EAA-1878: the placeholder Level sentinel is not a
                    // real role key — the raw string (now `unassigned-<app code>`)
                    // would otherwise leak into this dropdown verbatim.
                    label: isUnassignedRoleKey(role.role_key)
                        ? t('console.appDetail.accounts.noRoleAssigned')
                        : role.role_key,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [facets, t]
    );

    /**
     * EAA-1878 established that selection drives the retry. EAA-1839 (2026-08-24)
     * finishes the thought: the retry is now ONLY selection-driven, and it lives
     * in the shared `AccountsBulkBar` (which renders nothing until rows are
     * ticked) instead of in a bespoke button row of this tab's own.
     *
     * WHAT WAS DROPPED, deliberately: with no selection, the old button retried
     * every unhealthy row on the CURRENT PAGE. That was never a real unit of work
     * — "whatever this page happens to be showing" is a scroll position, not a
     * decision — and EAA-1878's own note above said as much while keeping it as a
     * fallback. Single-row retry is unaffected: the row kebab still offers it.
     * The genuine gap EAA-1888 recorded (no cross-org, app-wide sweep, because
     * that needed the full client-side row list this ticket removed) is unchanged
     * and still a follow-up.
     */
    const retryTargets = useMemo(() => {
        const picked = new Set(selectedKeys);
        return rows.filter((row) => picked.has(row.assignment_id) && !HEALTHY_OUTCOMES.has(row.outcome));
    }, [rows, selectedKeys]);

    // EAA-1878: the shared table's row kebab. Only an UNHEALTHY row gets one —
    // retrying a pushed/revoked/never-attempted row is a no-op the backend would
    // simply not requeue, and an always-present menu item that does nothing is
    // the kind of dead affordance this console keeps out of its tables.
    const rowActions = useCallback(
        (row: AccountTableRow): DropdownMenuItem[] => {
            if (HEALTHY_OUTCOMES.has(row.outcome)) return [];
            return [
                {
                    key: 'retry',
                    label: t('console.appDetail.accounts.retryRowAction'),
                    onSelect: () => retryMutation.mutate([row]),
                },
            ];
        },
        // `retryMutation` is declared below and is referentially stable for the
        // component's life (useMutation's own contract), so it is read here
        // without being listed as a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t]
    );

    const retryMutation = useMutation({
        // EAA-1888: scoped to the CURRENT PAGE's rows only — see this
        // component's own docstring for why the pre-1888 app-wide sweep
        // (every failing row across every org) is not reproduced here.
        mutationFn: async (targets: AccountTableRow[]) => {
            const byOrg = new Map<string, string[]>();
            for (const row of targets) {
                const ids = byOrg.get(row.organization.id) ?? [];
                ids.push(row.assignment_id);
                byOrg.set(row.organization.id, ids);
            }
            const results = await Promise.all(
                Array.from(byOrg.entries()).map(([orgId, assignment_ids]) =>
                    retryProvisioning(config.apiBase, accessToken as string, orgId, { assignment_ids })
                )
            );
            return results.reduce((sum, r) => sum + r.requeued, 0);
        },
        onSuccess: (requeued) => {
            setSelectedKeys([]);
            queryClient.invalidateQueries({ queryKey: ['app-accounts'] });
            toast.success(t('console.appDetail.accounts.retrySuccess', { count: requeued }));
        },
        onError: () => toast.error(t('console.appDetail.accounts.retryError')),
    });

    const retryCount = retryTargets.length;

    /* EAA-1839: which of the two width budgets applies — see
       `SIDEBAR_COLUMN_WIDTHS`. Read inside the `columns` memo below, so it is a
       dependency of it. */
    const widths = view.placement === 'sidebar' ? SIDEBAR_COLUMN_WIDTHS : COLUMN_WIDTHS;

    const columns = useMemo<ColumnConfig<AccountTableRow>[]>(() => {
        // Annotated as its own local rather than returned inline: `.filter()`
        // below breaks the contextual typing an inline array literal would get
        // from the `useMemo` type argument, so `align: 'right'` and
        // `dataIndex: 'sync_attempts'` would widen to `string`.
        const declared: ColumnConfig<AccountTableRow>[] = [
                {
                    key: 'displayName',
                    title: t('console.appDetail.accounts.columnAccount'),
                    // EAA-1888: NOT sortable — a display name is joined from the
                    // realm snapshot cache, not a DB column, so a real server sort
                    // by it would mean loading every row into memory first, the
                    // exact anti-pattern this ticket removes (see
                    // `AssignmentService.outcomes_for_app`'s own docstring).
                    // EAA-1878: the same avatar-initials identity cell the realm and
                    // org account tables use, so one person looks identical wherever
                    // they are listed. No `onClick`: there is no per-assignment detail
                    // read to open (the same reason `ScopedAccountsTable` passes no
                    // `onViewDetail`), and a button that goes nowhere is worse than
                    // plain text.
                    render: (_value, row) => (
                        <IdentityCell
                            primary={row.displayName}
                            secondary={row.user_email}
                        />
                    ),
                },
                {
                    key: 'organization',
                    title: t('console.appDetail.accounts.columnOrgScope'),
                    width: widths.organization,
                    render: (_value, row) =>
                        row.transfer_from_organization ? (
                            <div className={styles.accountsCell}>
                                <strong>
                                    {row.transfer_from_organization.name}
                                    <span aria-hidden="true"> → </span>
                                    {row.organization.name}
                                </strong>
                                <span className={`${detail.hint} ${styles.accountsTransferring}`}>
                                    {t('console.appDetail.accounts.transferring')}
                                </span>
                            </div>
                        ) : (
                            <div className={styles.accountsCell}>
                                <strong>{row.organization.name}</strong>
                                <span className={detail.hint}>{row.organization.code}</span>
                            </div>
                        ),
                },
                {
                    key: 'role',
                    title: t('console.appDetail.accounts.columnRole'),
                    render: (_value, row) =>
                        // EAA-1861 / EAA-1878: the placeholder Level sentinel (see
                        // `isUnassignedRoleKey`) — showing the raw code→arrow layout
                        // for it would leak an internal implementation detail as if it
                        // were a real, if unmapped, standard role.
                        isUnassignedRoleKey(row.role_key) ? (
                            <span className={detail.hint}>
                                {t('console.appDetail.accounts.noRoleAssigned')}
                            </span>
                        ) : (
                            <div className={detail.chipWrap}>
                                <code>{row.role_key}</code>
                                <span aria-hidden="true">→</span>
                                {row.appRoleLabel || t('console.appDetail.accounts.noAppRole')}
                            </div>
                        ),
                },
                {
                    key: 'outcome',
                    title: t('console.appDetail.accounts.columnOutcome'),
                    width: widths.outcome,
                    // EAA-1839: the raw enum (`never_attempted`, `no_mapping`, …) used to
                    // print on a second line under the translated label. It is redundant
                    // for anyone who can read the label and meaningless to anyone who
                    // cannot — and on a table where every row shares one outcome it made
                    // the column a wall of identical grey text. The `?? row.outcome`
                    // fallback below still surfaces the raw value in the ONE case where it
                    // carries information: an outcome the translation map does not know,
                    // which is exactly when a reader needs to see the server's own word.
                    render: (_value, row) => (
                        <StatusChip
                            tone={OUTCOME_CELL_TONE[row.outcome] ?? 'off'}
                            label={(outcomeConfig[row.outcome]?.label as string) ?? row.outcome}
                        />
                    ),
                },
                {
                    key: 'outcome_at',
                    title: t('console.appDetail.accounts.columnTimestamp'),
                    width: widths.outcome_at,
                    sorter: true,
                    // EAA-1888: the real DB column is `last_outcome_at` — the
                    // response's per-row field stays `outcome_at` (unchanged, see
                    // `ProvisioningOutcomeRow`), so the sort key must be named
                    // explicitly rather than defaulting to this column's own key.
                    sortKey: 'last_outcome_at',
                    render: (_value, row) =>
                        row.outcome_at ? new Date(row.outcome_at).toLocaleString('vi-VN') : '—',
                },
                {
                    key: 'sync_attempts',
                    title: t('console.appDetail.accounts.columnRetryCount'),
                    width: widths.sync_attempts,
                    dataIndex: 'sync_attempts',
                    align: 'right',
                    sorter: true,
                    render: (_value, row) => (
                        <span className={row.sync_attempts > 1 ? styles.accountsAttemptsHigh : undefined}>
                            {row.sync_attempts}
                        </span>
                    ),
                },
        ];
        // EAA-1839: the "Hiển thị" popover's column toggles. Filtered HERE
        // rather than by handing `DataTable` a `hidden` flag, because a hidden
        // column must also stop claiming its slice of the width budget above —
        // leaving it in place with `display: none` would keep reserving its
        // 148px and starve the identity column of exactly the width the reader
        // just freed up. `displayName` is never filtered: it is not offered as
        // a toggle (see `useAppDetailAccountsView`).
        return declared.filter(
            (column) => column.key === 'displayName' || isColumnVisible(column.key)
        );
    }, [t, outcomeConfig, isColumnVisible, widths]);

    /**
     * EAA-1839 (2026-08-24, user request): THE FILTERS ARE THE SHARED CHROME'S
     * FILTERS. Three `FacetGroup`s handed to the shared `AccountsFacetRail`,
     * replacing the three `AppsFilterButton` dropdowns and the six-chip outcome
     * row this tab used to draw itself.
     *
     * All three landed cleanly, because all three were already what a facet
     * group is: an enumerable option list with a server-side count that does NOT
     * narrow with the current filter (`AssignmentService.outcomes_for_app`
     * guarantees that — see the `facets` comment above). No count is invented:
     * `?? null` where the facets have not arrived, which the rail renders as "—"
     * rather than as a zero, per `FacetOption.count`'s own contract.
     *
     * `tone: 'warn'` on the unhealthy outcomes is the one thing added rather than
     * mapped: it is the same `HEALTHY_OUTCOMES` split the retry action uses, so
     * the rail marks exactly the rows the retry button would act on.
     *
     * The "Tất cả" row every old dropdown carried is gone, and nothing replaced
     * it: a facet checkbox toggles, so re-ticking the selected option clears the
     * group (see `handleSelectFacet`), and the rail's own "Xoá bộ lọc" button
     * clears everything. Two affordances for one action is how a screen starts
     * disagreeing with itself.
     */
    const facetGroups = useMemo<FacetGroup[]>(
        () => [
            {
                key: 'outcome',
                label: t('console.appDetail.accounts.columnOutcome'),
                options: (Object.keys(outcomeConfig) as ProvisioningOutcome[]).map((key) => ({
                    key,
                    label: outcomeConfig[key].label as string,
                    // `null` ONLY while the facets are genuinely unknown (first
                    // load, or an errored facet read). Once they have arrived, an
                    // outcome the server did not list has zero rows — it enumerates
                    // every outcome present over the whole app-relevant set — so a
                    // real 0 is the honest value and "—" would be a false "unknown".
                    count: facets ? (outcomeCounts[key] ?? 0) : null,
                    selected: vm.accountsOutcomeFilter === key,
                    tone: HEALTHY_OUTCOMES.has(key) ? undefined : ('warn' as const),
                })),
            },
            {
                key: 'org',
                label: t('console.appDetail.accounts.columnOrgScope'),
                options: orgOptions.map((org) => ({
                    key: org.id,
                    label: org.name,
                    count: org.count ?? null,
                    selected: vm.accountsOrgFilter === org.id,
                })),
            },
            {
                key: 'role',
                label: t('console.appDetail.accounts.columnRole'),
                options: roleOptions.map((role) => ({
                    key: role.key,
                    label: role.label,
                    count: role.count ?? null,
                    selected: vm.accountsRoleFilter === role.key,
                })),
            },
        ],
        [
            t,
            outcomeConfig,
            facets,
            outcomeCounts,
            orgOptions,
            roleOptions,
            vm.accountsOutcomeFilter,
            vm.accountsOrgFilter,
            vm.accountsRoleFilter,
        ]
    );

    /**
     * EAA-1839: SINGLE-SELECT, TOGGLE-TO-CLEAR — the org board's semantics, not
     * the realm board's. Each of this tab's three server params
     * (`outcome`/`organization`/`role`) takes ONE exact value, so a second tick
     * in the same group replaces the first rather than OR-ing with it; ticking
     * the selected option again clears the group. The rail deliberately knows
     * none of this (`AccountsFacetRail`'s own docstring): it always calls
     * `onSelect(groupKey, optionKey)` and leaves the semantics to the adapter,
     * which is this function.
     */
    const handleSelectFacet = useCallback(
        (groupKey: string, optionKey: string) => {
            if (groupKey === 'outcome') {
                const next = optionKey as ProvisioningOutcome;
                vm.setAccountsOutcomeFilter(vm.accountsOutcomeFilter === next ? null : next);
                return;
            }
            if (groupKey === 'org') {
                vm.setAccountsOrgFilter(vm.accountsOrgFilter === optionKey ? null : optionKey);
                return;
            }
            if (groupKey === 'role') {
                vm.setAccountsRoleFilter(vm.accountsRoleFilter === optionKey ? null : optionKey);
            }
        },
        // The three setters are `useState` setters and stable; the three current
        // values are read to decide toggle-vs-set.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [vm.accountsOutcomeFilter, vm.accountsOrgFilter, vm.accountsRoleFilter]
    );

    /**
     * EAA-1839 (2026-08-24, user request): the rail, collapsed behind the shared
     * toolbar's "Bộ lọc (N)" trigger — the `filterTrigger` slot, rendering the
     * SAME `FilterPopover` + `AccountsFacetRail variant="popover"` pair
     * `AccountsScreen` renders for its `'header'` placement. Copied deliberately
     * from there rather than imported, because `AccountsScreen` itself cannot be
     * used here: it owns a page-level tab strip, and this tab already lives
     * inside `AppDetailPage`'s one.
     *
     * A SIDEBAR rail is what frame 35 draws and what the boards default to, and
     * it is not used here: the rail column costs 236px of width, and this tab's
     * table has four fixed columns plus two flexing ones inside a 1228px
     * container at 1366px (see `COLUMN_WIDTHS`) — taking 236px off that would put
     * the flexing pair at ~100px each. The popover placement was built for
     * exactly this trade-off.
     *
     * AND THE SYNC-USERS BUTTON RIDES ALONG IN THE SAME SLOT, which is a
     * compromise worth naming: `AccountsToolbar` has no caller-owned ACTION slot
     * — `filterTrigger` is the only `ReactNode` it accepts — and the alternative
     * was to keep this tab's own row of controls under the toolbar, which is the
     * parallel toolbar the user asked to remove. That `actions?: ReactNode` prop
     * now EXISTS on `AccountsToolbar` (added straight after this pass), so the
     * button rides in it and `filterTrigger` carries only the filter again.
     */
    /* EAA-1839 (2026-08-24, user request): the rail's two placements. `railProps`
       is the single source for both so the two renderings can never drift into
       showing different facets — the only difference between them is `variant`,
       which is exactly what the shared component's own prop exists for. */
    const railProps = {
        groups: facetGroups,
        onSelect: handleSelectFacet,
        hasActiveFilters: filtersActive,
        onClear: clearFilters,
        /* The rail carries every organization and every role in the facets, so it
           needs its own client-side option filter for the same reason the org
           board's does — it narrows the RAIL, never the table. */
        quickFilter: { value: railQuery, onChange: setRailQuery },
        note: t('console.accounts.facetRail.cumulativeNote'),
    };

    const sidebarRail = <AccountsFacetRail variant="sidebar" {...railProps} />;

    const filterTrigger = (
        <FilterPopover
            align="start"
            width={280}
            panelClassName="acc-filterbar__panel"
            trigger={({ open, ref, toggle }) => (
                <Button
                    ref={ref}
                    icon={<FilterOutlined />}
                    aria-expanded={open}
                    aria-label={t('console.accounts.filters.filterTriggerAriaLabel', {
                        count: activeFilterCount,
                    })}
                    onClick={toggle}
                >
                    {t('console.accounts.filters.filterTriggerButton', {
                        count: activeFilterCount,
                    })}
                </Button>
            )}
            >
            <AccountsFacetRail variant="popover" {...railProps} />
        </FilterPopover>
    );

    // EAA-1839: this tab's own action, in the toolbar's `actions` slot — added for
    // exactly this case, so an action no longer has to ride inside `filterTrigger`
    // on the wrong side of the spacer.
    const toolbarActions = onSyncUsers ? (
        <Button onClick={onSyncUsers}>{t('console.appDetail.accounts.pullUsersButton')}</Button>
    ) : undefined;

    const app = vm.app;
    if (!app) return null;

    if (vm.accountsError) {
        return (
            <SectionCard>
                <ErrorState description={t('console.appDetail.accounts.loadError')} />
            </SectionCard>
        );
    }

    return (
        // EAA-1840: NOT also `.fit-grow` — `AppDetailPage.tsx`'s own
        // `.app-detail-tab-panel` already carries that class for every tab.
        // Doubling it here made the fit-to-shell global rule
        // (`.portal-console-page--fit .fit-grow > *`) match a SECOND time
        // against this div's own children, turning the plain footer `<p>`
        // below into a flex-growing box that silently ate most of the
        // available height instead of the table.
        <div
            className={
                styles.accountsTab +
                (view.placement === 'sidebar' ? ` ${styles.accountsTabSidebar}` : '')
            }
        >
            {/* EAA-1839 (2026-08-24, user request): the facet rail as a standing
                236px column, the "Thanh bên" half of the "VỊ TRÍ BỘ LỌC" section.
                It is a SIBLING of the toolbar and the card rather than a wrapper
                around them, so the modifier class only has to turn this flex
                column into a two-column grid and place three children — no extra
                nesting, so nothing new lands between `.fit-grow` and the card that
                the fit-to-shell chain has to be re-taught. */}
            {view.placement === 'sidebar' && sidebarRail}
            {/* EAA-1839: the SHARED board toolbar, in place of this tab's own
                hand-rolled search row. See the import comment for what it brings. */}
            <AccountsToolbar
                search={searchInput}
                onSearchChange={setSearchInput}
                /* This tab's own wording survives the swap: the shared placeholder
                   ("Tìm theo tên, username hoặc email…") over-promises here, where
                   the server searches name and email only. */
                searchPlaceholder={t('console.appDetail.accounts.searchPlaceholder')}
                searchAriaLabel={t('console.appDetail.accounts.searchPlaceholder')}
                activeFilterCount={activeFilterCount}
                onClearFilters={clearFilters}
                /* Only in the 'header' placement: with the rail standing open in
                   its own column, a popover holding the same rail would be a
                   second copy of a control already on screen. */
                filterTrigger={view.placement === 'header' ? filterTrigger : undefined}
                actions={toolbarActions}
                columns={{ items: view.columnItems, onToggle: view.toggleColumn }}
                density={{ value: view.density, onChange: view.setDensity }}
                /* EAA-1839 (2026-08-24, user request): "VỊ TRÍ BỘ LỌC" — the same
                   third section the two accounts boards show. This comment used to
                   say the control would be inert here because "this tab has no
                   facet rail"; that was already untrue when it was written (the
                   "Bộ lọc (N)" trigger IS the rail, popover-placed), and the tab
                   now renders the sidebar placement too. What is genuinely
                   different here is the DEFAULT — `'header'`, not `'sidebar'` —
                   and the fact that the sidebar costs a narrower column budget
                   (`SIDEBAR_COLUMN_WIDTHS`). `AccountsScreen` owns this switch on
                   the boards; this tab cannot use that component (it owns a
                   page-level tab strip) so it wires the same two renderings
                   itself. */
                placement={{ value: view.placement, onChange: view.setPlacement }}
                onRefresh={handleRefresh}
                refreshBusy={accountsFetching}
            />

            {/* EAA-1839: `bodyClassName` instead of the `.app-detail-page .ant-card-body > …`
                descendant selectors 24-app-detail.css used to reach this card's body with.
                The page-local spacing intent is unchanged; only the mechanism is, so it no
                longer breaks if SectionCard's internals move. */}
            <SectionCard
                bodyPadded={false}
                className={styles.accountsCard}
                bodyClassName={styles.accountsCardBody}
            >
                {vm.accountsTotal === 0 && !vm.accountsLoading ? (
                    <EmptyState
                        title={t('console.appDetail.accounts.emptyTitle')}
                        description={
                            filtersActive
                                ? t('console.appDetail.accounts.emptyFilteredDescription')
                                : t('console.appDetail.accounts.emptyDescription')
                        }
                        action={
                            !filtersActive && onSyncUsers ? (
                                <Button onClick={onSyncUsers}>
                                    {t('console.appDetail.accounts.pullUsersButton')}
                                </Button>
                            ) : undefined
                        }
                    />
                ) : (
                    <>
                        {/* EAA-1839 (2026-08-24, user request): the multi-row retry
                            moved out of this tab's own control row and into the SHARED
                            selection bar, which is where every other accounts board
                            puts a multi-row action. It renders nothing until rows are
                            ticked, so it costs no height in the default state. */}
                        <AccountsBulkBar
                            selectedCount={selectedKeys.length}
                            onClear={() => setSelectedKeys([])}
                            busy={retryMutation.isPending}
                            actions={[
                                {
                                    key: 'retry',
                                    label: retryMutation.isPending
                                        ? t('console.appDetail.accounts.retryPending')
                                        : t('console.appDetail.accounts.retryButton', {
                                              count: retryCount,
                                          }),
                                    onClick: () => retryMutation.mutate(retryTargets),
                                    // A selection of healthy rows has nothing to retry.
                                    // No `disabledReason`: every string on this screen
                                    // has to already exist (this pass owns no locale
                                    // file), and the zero in the label already says it.
                                    disabled: retryCount === 0,
                                },
                            ]}
                        />
                        <SharedAccountsTable<AccountTableRow>
                            rows={rows}
                            columns={columns}
                            rowKey="assignment_id"
                            /* EAA-1839: user-draggable, per-viewer persisted column
                               widths under THIS table's own key
                               (`ui:colwidths:app-detail-accounts`) — distinct from
                               `realm-accounts` and `scoped-accounts` because the three
                               boards have different column sets and the kit's
                               `useResizableColumns` would otherwise let each overwrite
                               the others'. No `version` yet: bump it only if the column
                               set is reshaped enough that stored widths would land on
                               the wrong columns. */
                            resize={{ storageKey: APP_DETAIL_ACCOUNTS_TABLE_KEY }}
                            /* EAA-1839: "Mật độ" — only the class changes; the rule is
                               `.acc-table--compact` in `15-accounts.css`, which is
                               unscoped and so applies here exactly as on the boards. */
                            className={`acc-table--${view.density}`}
                            selectedKeys={selectedKeys}
                            onSelectedKeysChange={setSelectedKeys}
                            onSortChange={vm.setAccountsOrdering}
                            rowActions={rowActions}
                            rowActionsAriaLabel={(row) =>
                                t('console.appDetail.accounts.rowActionsLabel', {
                                    name: row.displayName || row.user_email,
                                })
                            }
                            emptyText={t('console.appDetail.accounts.emptyTitle')}
                        />
                        <AccountsPagination
                            page={vm.accountsPage}
                            totalPages={vm.accountsTotalPages}
                            pageSize={vm.accountsPageSize}
                            onPageSizeChange={vm.setAccountsPageSize}
                            rangeFrom={rangeFrom}
                            rangeTo={rangeTo}
                            total={vm.accountsTotal}
                            onGoTo={vm.setAccountsPage}
                        />
                    </>
                )}
            </SectionCard>
            {/* EAA-1839 (2026-08-24, user request): the summary line that used to sit
                here is REMOVED — `console.appDetail.accounts.footerSummary`, rendered
                as e.g. "124 tài khoản có quyền trong EMG English LMS · 0 chưa cấp
                được". Its `total` was pure duplication (the toolbar's range readout
                shows "Hiển thị 124/124" and the pager shows "… trong 124"), so nothing
                is lost there. Its `needsAttention` (`vm.accountNeedsAttention`, i.e.
                the facets' `needs_attention`) was the ONE place that aggregate
                appeared: its per-outcome components are still visible, and now
                warn-toned, in the shared facet rail ("Lỗi", "Chưa liên kết vai trò",
                …), but the single summed figure is gone. Flagged to the user rather
                than quietly replaced. The two i18n keys are deliberately left in the
                locale files — this pass owns none of them. */}
        </div>
    );
}

export default AppDetailAccountsTab;
